import { BASE_ROUND_POINTS, perfectScore } from './duelScoring';

/**
 * A row on the shared board, and what makes one publishable (SPEC-009 PR-9.1).
 *
 * THIS MODULE MIRRORS A FILE IN ANOTHER REPOSITORY. The bounds below are
 * enforced for real in `~/projects/pwa/firestore.rules`, under
 * `match /legends/leaderboard/entries/{uid}` — Legends may not deploy rules
 * (CON-003), so the authorisation for its own collection lives in the repo that
 * owns the single ruleset. A rules file cannot import this module, so the two
 * are duplicates held together by nothing but their comments and the emulator
 * suite over there. If a write starts being rejected in production, this is the
 * first place to look and that file is the second.
 *
 * Checking here as well as there is not belt-and-braces. A rejected write is a
 * promise this product cannot keep quietly — the reader finished a run, the
 * screen said it was being published, and a permission error arrives a second
 * later with nothing useful to say. Validating first means the client can
 * decline to send a row it knows is wrong and explain why, and it means a
 * rejection that DOES arrive is a genuine bug rather than a routine outcome.
 *
 * Pure: no Firebase, no React, no clock. The `updatedAt` stamp is deliberately
 * not modelled here — it is the server's, written as a sentinel by the
 * infrastructure layer, because the rules require it to equal `request.time`
 * and a value this layer invented could only ever be wrong.
 */

/** The one shape ever written. A second version is a migration, not a guess. */
export const ENTRY_SCHEMA_VERSION = 1;

/**
 * How many rows the board shows.
 *
 * Five, the same as the device board — the two are drawn in the same slot at
 * the end of a run, and a shared board that was suddenly twice as tall would
 * push the reveal off a screen SPEC-007 committed to not scrolling.
 */
export const BOARD_SIZE = 5;

/**
 * How many rows are FETCHED, as against how many are drawn (SPEC-009 D-9).
 *
 * The board shows five and the reader wants to know their own place, which is
 * usually not in the five. Firestore could answer "how many rows beat mine?"
 * exactly with an aggregate count — but `getCountFromServer` is not in the lite
 * SDK, and pulling in the full one to number a card game's board would cost more
 * bundle than the whole feature is worth (D-6).
 *
 * So a window is read and ranked here. Fifty is chosen to be far past the number
 * of people who will ever play this, so that in practice every reader sees a
 * real placing. A reader beyond it is told they are outside the top fifty rather
 * than shown a number this module cannot stand behind — an honest gap, and the
 * one case where this feature says less than it would like to.
 */
export const BOARD_WINDOW = 50;

/**
 * The abuse bounds, which are not the same thing as the scoring rules.
 *
 * A streak of 500 is far beyond anything a person will reach against two dozen
 * cars; that is the point. These exist to keep a hostile write from being
 * *unbounded*, not to describe a plausible run — a ceiling tight enough to be
 * realistic would eventually deny somebody an honest record, which is a support
 * conversation nobody wants about a card game.
 */
export const MAX_STREAK = 500;

/** A provider display name, capped. Long enough for a real name and no more. */
export const MAX_DISPLAY_NAME = 60;

/**
 * Whether a score could have come from a run of this length.
 *
 * `duelScoring.js` pays at least `BASE_ROUND_POINTS` for each correct round, so
 * n correct answers is worth at least 100n. The ceiling is `perfectScore(n)`
 * rather than n times anything: rounds are not worth the same as each other —
 * the profile pays more for a harder question later in a run — so a run of five
 * can only have earned the five CHEAPEST ceilings. Multiplying by the dearest
 * would wave through a forgery; multiplying by the cheapest would call an honest
 * fast run on the tenth round one.
 *
 * Both ends come from that module rather than being written here, so a tier
 * added to `SPEED_MULTIPLIERS` or a row added to the difficulty curve cannot
 * quietly make honest runs implausible. This is the one check in the feature that
 * costs a cheat anything: the figures have to agree with each other, so a
 * claimed score drags a claimed streak up with it and both are visible on the
 * board. It does not make a score TRUE — ADR-0005 says so plainly and so does
 * the product copy — it makes it plausible.
 */
export function isConsistentRun({ score, streak }) {
    return score >= streak * BASE_ROUND_POINTS && score <= perfectScore(streak);
}

function isWholeNumber(value) {
    return Number.isInteger(value);
}

/**
 * Whether this run may be sent.
 *
 * Everything is checked, including the things that "cannot" be wrong: the
 * display name arrives from an auth provider and could be absent, and the score
 * arrives from a run that could have ended at zero. A run worth nothing is not
 * a row — the same judgement `recordRun` makes about the device board, for the
 * same reason.
 */
export function isPublishableEntry(entry) {
    if (!entry || typeof entry !== 'object') {
        return false;
    }

    const { displayName, score, streak } = entry;

    return typeof displayName === 'string'
        && displayName.length >= 1
        && displayName.length <= MAX_DISPLAY_NAME
        && isWholeNumber(score)
        && isWholeNumber(streak)
        && streak >= 1
        && streak <= MAX_STREAK
        && isConsistentRun({ score, streak });
}

/**
 * The row for a finished run, or null if it is not one worth publishing.
 *
 * The name is trimmed and clipped rather than rejected at the cap: a provider
 * returning something long is not the reader's mistake and refusing to publish
 * their run over it would be a bug they cannot do anything about. A name that
 * is empty AFTER trimming is a different matter — there is nothing to draw, and
 * the caller falls back to the local part of the email address before it gets
 * here.
 */
export function buildEntry({ displayName, score, streak }) {
    const name = typeof displayName === 'string' ? displayName.trim().slice(0, MAX_DISPLAY_NAME) : '';
    const entry = {
        schemaVersion: ENTRY_SCHEMA_VERSION,
        displayName: name,
        score: Math.trunc(Number(score)),
        streak: Math.trunc(Number(streak)),
    };

    return isPublishableEntry(entry) ? entry : null;
}

/**
 * Whether a finished run beats what is already published.
 *
 * Strictly greater, matching the rules exactly: an update that merely ties is
 * refused server-side, so a client that sent one would be writing a request it
 * knows will fail. A tie changes nothing a reader can see anyway — the row is
 * already there, at the same number.
 */
export function beatsPublished(entry, published) {
    if (!entry) {
        return false;
    }

    return !published || entry.score > published.score;
}

/**
 * A list of rows from the network, as a board the screen can draw.
 *
 * Three things happen here and they are all presentation rather than storage,
 * which is why they are in the domain and not in the client: unreadable rows are
 * dropped, the rest are ordered, and the reader's own row is found — wherever it
 * is, including below the cut.
 *
 * `standing` is the answer to the only question a reader actually has, and it is
 * separate from `rows` on purpose. A reader who is fourteenth wants to know they
 * are fourteenth; a board that showed five strangers and said nothing about them
 * would be the version of this feature that makes somebody feel worse for having
 * signed in.
 *
 * @returns {{rows: Array, standing: {rank: number, entry: object}|null}}
 *   `rows` is the top `BOARD_SIZE`, each carrying `rank` and `isMine`.
 *   `standing` is the reader's own place, or null if they have no row yet.
 */
export function rankBoard(entries, { uid = null, size = BOARD_SIZE } = {}) {
    const ranked = (Array.isArray(entries) ? entries : [])
        .filter((entry) => isPublishableEntry(entry) && typeof entry.uid === 'string')
        // Score descending, and ties broken by the OLDER row first. Two runs
        // tie often enough at the foot of a five-row board that leaving the
        // order to the source would let rows swap places between two reads for
        // no reason a reader could see.
        .sort((one, other) => other.score - one.score || compareStamps(one, other))
        .map((entry, place) => ({ ...entry, rank: place + 1, isMine: entry.uid === uid }));

    const mine = ranked.find((entry) => entry.isMine) ?? null;

    return {
        rows: ranked.slice(0, size),
        standing: mine ? { rank: mine.rank, entry: mine } : null,
    };
}

/**
 * Older first, for rows that tie.
 *
 * `updatedAt` is whatever the client layer handed over — a Firestore timestamp
 * turned into milliseconds, or nothing at all for a row written before the
 * field existed. A missing stamp sorts last rather than throwing: the board is
 * read from a collection this app does not control the history of.
 */
function compareStamps(one, other) {
    const first = Number.isFinite(one.updatedAt) ? one.updatedAt : Number.POSITIVE_INFINITY;
    const second = Number.isFinite(other.updatedAt) ? other.updatedAt : Number.POSITIVE_INFINITY;

    return first - second;
}
